import React from 'react';
import { Container, Typography, Box, TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import profile from '../assets/profile.png';
import logo from '../assets/logo0.png';

const Privacy = () => {
  const sections = [
    {
      title: "1. Information We Collect",
      content: [
        "Personal Information: Name, email address, phone number, and payment details when you register or make transactions.",
        "Device & Usage Data: Information about your device, IP address, browser type, and interaction with our platform.",
        "Uploaded Content: Images, descriptions, and data related to your devices for AI-based fault detection.",
        "Cookies & Tracking Technologies: We use cookies to enhance your experience and analyze platform usage."
      ]
    },
    {
      title: "4. Data Security",
      content: "We take reasonable security measures to protect your data from unauthorized access, alteration, and misuse. However, no internet transmission is 100% secure, and users should take precautions when sharing sensitive information."
    },
    {
      title: "5. Your Rights & Choices",
      content: [
        "Depending on your location, you may have rights to:",
        "Access & Update Data: Review and correct your personal information.",
        "Delete Data: Request deletion of your data, subject to legal obligations.",
        "Opt-Out: Manage preferences for marketing communications and cookies."
      ]
    },
    {
      title: "6. Third-Party Services & Links",
      content: "Our platform may contain links to third-party websites. We are not responsible for their privacy policies or practices. Users should review third-party policies before providing personal information."
    },
    {
      title: "7. Children's Privacy",
      content: "Larrvarris AI Solutions is not intended for children under 13. We do not knowingly collect data from minors. If a parent or guardian believes their child has provided personal data, they should contact us for removal."
    },
    {
      title: "8. Changes to This Privacy Policy",
      content: 'We may update this Privacy Policy periodically. Any changes will be posted on our platform with the "Last Updated" date. We encourage users to review the policy regularly.'
    }
  ];

  // Add table of contents items
  const tableOfContents = [
    { id: 'info-collect', title: '1. Information We Collect' },
    { id: 'info-use', title: '2. How We Use Your Information' },
    { id: 'sharing', title: '3. Sharing & Disclosure' },
    { id: 'security', title: '4. Data Security' },
    { id: 'rights', title: '5. Your Rights & Choices' },
    { id: 'third-party', title: '6. Third-Party Services' },
    { id: 'children', title: "7. Children's Privacy" },
    { id: 'changes', title: '8. Changes to Policy' }
  ];

  // Add this right before the table of contents dots
  const TableOfContents = () => (
    <Box sx={{ 
      position: 'fixed',
      right: '5%',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      <Typography sx={{
        color: '#000000',
        fontSize: '16px',
        fontWeight: 600,
        mb: 2
      }}>
        Table of content
      </Typography>
      {tableOfContents.map((item, index) => (
        <Box
          key={index}
          onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              '& .arrow': {
                opacity: 1,
                width: '24px',
              },
              '& .dot': {
                bgcolor: '#000000',
              },
              '& .title': {
                opacity: 1,
              }
            }
          }}
        >
          <Box className="title" sx={{
            opacity: 0,
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            color: '#000000',
            fontSize: '14px',
            mr: 1,
            fontWeight: 500
          }}>
            {item.title}
          </Box>
          <Box className="arrow" sx={{
            opacity: 0,
            width: 0,
            height: '2px',
            bgcolor: '#000000',
            transition: 'all 0.3s ease',
            mr: 1
          }} />
          <Box className="dot" sx={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            bgcolor: '#666666',
            transition: 'all 0.3s ease'
          }} />
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            position: 'relative',
            pt: 8
          }}>
            <Box sx={{ maxWidth: '600px' }}>
              <Typography variant="h1" sx={{ 
                fontSize: '64px',
                fontWeight: 700,
                color: '#000000',
                mb: 3,
                fontFamily: 'serif'
              }}>
                Centre d'aide
              </Typography>

              <Typography sx={{ 
                fontSize: '18px',
                color: '#333333',
                mb: 4,
                lineHeight: 1.6
              }}>
                Welcome to Larrvarris AI Solutions ("we," "our," or "us"). Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our platform, applications, and services.
              </Typography>

              {/* Search Box */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                maxWidth: '800px'
              }}>
                <TextField
                  fullWidth
                  placeholder="Comment pouvons-nous vous aider ?"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#ffffff',
                      height: '56px',
                      '& fieldset': {
                        borderColor: '#e0e0e0'
                      }
                    }
                  }}
                />
                <IconButton 
                  sx={{ 
                    position: 'absolute',
                    right: 8,
                    bgcolor: '#000000',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: '#333333'
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Right Image */}
            <Box sx={{ 
              position: 'absolute',
              right: 0,
              top: '-40px'
            }}>
              <img 
                src={profile} 
                alt="Support" 
                style={{
                  height: '500px',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>

          {/* Add Table of Contents */}
          <TableOfContents />

          {/* Privacy Policy Content - Add IDs to sections */}
          <Box sx={{ maxWidth: '800px', mt: 8, mb: 8 }}>
            {sections.map((section, index) => (
              <Box 
                key={index} 
                id={tableOfContents[index].id}
                sx={{ mb: 5 }}
              >
                <Typography variant="h5" sx={{ 
                  color: '#000000',
                  fontWeight: 600,
                  mb: 3
                }}>
                  {section.title}
                </Typography>
                {Array.isArray(section.content) ? (
                  section.content.map((item, i) => (
                    <Typography key={i} sx={{ 
                      color: '#333333',
                      mb: 2,
                      pl: 2
                    }}>
                      • {item}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ 
                    color: '#333333',
                    mb: 2,
                    pl: 2
                  }}>
                    {section.content}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Black Footer with White Logo */}
      <Box sx={{ 
        bgcolor: '#000000', 
        py: 4,
        borderTop: '1px solid #333333'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <img 
              src={logo} 
              alt="Larrvarris Logo" 
              style={{
                height: '40px',
                filter: 'brightness(0) invert(1)' // Convert logo to white
              }}
            />
            <Typography sx={{ color: '#ffffff', fontSize: '14px' }}>
              © 2024 Larrvarris AI Solutions. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Privacy;